import { useCallback, useEffect, useMemo, useState } from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import type { TextFieldProps } from "@mui/material/TextField";
import config from "../../data/config";

type LookupStatus =
  | "idle"
  | "loading"
  | "success"
  | "wrongStatus"
  | "notFound"
  | "serverError";

type TokenLookupResponse = {
  issuerId: string;
  tokenId: string;
  status: string;
  tokenAddress: string;
  deploymentId: string;
  provider: string;
};

type Props = {
  network: string;
  value: string;
  onChange: (value: string) => void;
  onIssuerChange?: (issuerId: string) => void;
} & Pick<
  TextFieldProps,
  | "label"
  | "required"
  | "fullWidth"
  | "size"
  | "helperText"
  | "placeholder"
  | "disabled"
  | "InputProps"
>;

const ETH_ADDRESS_REGEX = /^0x[a-fA-F0-9]{40}$/;

const DS_TOKEN_SUCCESS_MESSAGE = "DS Token Found in success status";
const DS_TOKEN_WRONG_STATUS_MESSAGE = "DS Token Found in wrong status";
const DS_TOKEN_NOT_FOUND_MESSAGE = "DS Token Not Found";
const DS_TOKEN_ERROR_MESSAGE = "Error checking DSToken";

export function DSTokenAddressChecker(props: Props) {
  const {
    network,
    value,
    onChange,
    onIssuerChange,
    helperText,
    InputProps: inputProps,
    disabled,
    ...rest
  } = props;

  const [status, setStatus] = useState<LookupStatus>("idle");

  useEffect(() => {
    if (!network || !value || !ETH_ADDRESS_REGEX.test(value)) {
      setStatus("idle");
      return;
    }
  }, [network, value]);

  const checkToken = useCallback(
    async (address: string, provider: string) => {
      if (!provider || !ETH_ADDRESS_REGEX.test(address)) {
        setStatus("idle");
        if (onIssuerChange) {
          onIssuerChange("");
        }
        return;
      }

      const baseUrl = config.PDM_API_BASE_URL.replace(
        /\/$/,
        "",
      );
      if (!baseUrl) {
        setStatus("serverError");
        if (onIssuerChange) {
          onIssuerChange("");
        }
        return;
      }

      setStatus("loading");
      try {
        // eslint-disable-next-line max-len
        const url = `${baseUrl}/providers/${encodeURIComponent(provider)}/tokens?address=${encodeURIComponent(address)}`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            accept: "application/json",
          },
        });

        if (response.status === 404) {
          setStatus("notFound");
          if (onIssuerChange) {
            onIssuerChange("");
          }
          return;
        }

        if (response.status >= 500) {
          setStatus("serverError");
          if (onIssuerChange) {
            onIssuerChange("");
          }
          return;
        }

        if (!response.ok) {
          setStatus("serverError");
          if (onIssuerChange) {
            onIssuerChange("");
          }
          return;
        }

        const data = (await response.json()) as TokenLookupResponse;

        if (onIssuerChange) {
          onIssuerChange(data.issuerId ?? "");
        }

        if (data.status === "success") {
          setStatus("success");
        } else {
          setStatus("wrongStatus");
        }
      } catch (_error) {
        setStatus("serverError");
        if (onIssuerChange) {
          onIssuerChange("");
        }
      }
    },
    [onIssuerChange],
  );

  useEffect(() => {
    let handle: number | undefined;

    if (network && value && ETH_ADDRESS_REGEX.test(value)) {
      handle = window.setTimeout(() => {
        void checkToken(value, network);
      }, 400);
    }

    return () => {
      if (handle !== undefined) {
        window.clearTimeout(handle);
      }
    };
  }, [checkToken, network, value]);

  const { statusMessage, statusIcon } = useMemo(() => {
    switch (status) {
      case "success":
        return {
          statusMessage: DS_TOKEN_SUCCESS_MESSAGE,
          statusIcon: (
            <CheckCircleOutlineIcon
              color="success"
              fontSize="small"
              aria-label="DS token found in success status"
            />
          ),
        };
      case "wrongStatus":
        return {
          statusMessage: DS_TOKEN_WRONG_STATUS_MESSAGE,
          statusIcon: (
            <WarningAmberIcon
              color="warning"
              fontSize="small"
              aria-label="DS token found in wrong status"
            />
          ),
        };
      case "notFound":
        return {
          statusMessage: DS_TOKEN_NOT_FOUND_MESSAGE,
          statusIcon: (
            <WarningAmberIcon
              color="warning"
              fontSize="small"
              aria-label="DS token not found"
            />
          ),
        };
      case "serverError":
        return {
          statusMessage: DS_TOKEN_ERROR_MESSAGE,
          statusIcon: (
            <WarningAmberIcon
              color="warning"
              fontSize="small"
              aria-label="Error checking DS token"
            />
          ),
        };
      case "loading":
        return {
          statusMessage: undefined,
          statusIcon: (
            <CircularProgress
              size={18}
              aria-label="Checking DS token address"
            />
          ),
        };
      case "idle":
      default:
        return { statusMessage: undefined, statusIcon: undefined };
    }
  }, [status]);

  const combinedHelperText = useMemo(() => {
    if (!statusMessage) {
      return helperText;
    }
    if (!helperText) {
      return statusMessage;
    }
    return (
      <>
        {helperText}
        <br />
        {statusMessage}
      </>
    );
  }, [helperText, statusMessage]);

  const endAdornment = useMemo(() => {
    if (!statusIcon) {
      return inputProps?.endAdornment ?? null;
    }
    return (
      <InputAdornment position="end">
        {inputProps?.endAdornment}
        {statusIcon}
      </InputAdornment>
    );
  }, [inputProps, statusIcon]);

  return (
    <TextField
      {...rest}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      helperText={combinedHelperText}
      disabled={disabled || !network}
      InputProps={{
        ...inputProps,
        endAdornment,
      }}
    />
  );
}

